import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";
import { useTransition, type FormEvent } from "react";

import { Button } from "@/components/ui/Button";
import type { ArticleLibraryTab, ArticleLibrarySortState } from "@/core/services/types/article.types";
import { buildArticleLibraryHref } from "@/core/services/types/article.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";

const searchInputClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function SearchSpinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
    />
  );
}

interface ArticlesLibrarySearchFormProps {
  tab: ArticleLibraryTab;
  searchQuery: string;
  sort: ArticleLibrarySortState;
  pageSize: LibraryPageSize;
}

function buildLibraryHref(
  tab: ArticleLibraryTab,
  sort: ArticleLibrarySortState,
  pageSize: LibraryPageSize,
): string {
  return buildArticleLibraryHref({ tab, page: 1, sort, pageSize });
}

export function ArticlesLibrarySearchForm({
  tab,
  searchQuery,
  sort,
  pageSize,
}: ArticlesLibrarySearchFormProps) {
  const t = useTranslations("articles.library");
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const query = String(formData.get("q") ?? "").trim();
    const params = new URLSearchParams({ status: tab, page: "1" });

    if (query) {
      params.set("q", query);
    }

    startTransition(() => {
      navigate(
        buildArticleLibraryHref({
          tab,
          page: 1,
          searchQuery: query,
          sort,
          pageSize,
        }),
      );
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 border-b border-black/10 px-4 py-3 sm:flex-row sm:items-center"
    >
      <label className="sr-only" htmlFor="articles-library-search">
        {t("searchLabel")}
      </label>
      <input
        id="articles-library-search"
        type="search"
        name="q"
        defaultValue={searchQuery}
        placeholder={t("searchPlaceholder")}
        disabled={isPending}
        className={searchInputClassName}
      />
      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="submit"
          disabled={isPending}
          aria-busy={isPending}
        >
          {isPending ? <SearchSpinner /> : null}
          {t("searchSubmit")}
        </Button>
        {searchQuery ? (
          <Link
            to={buildLibraryHref(tab, sort, pageSize)}
            className="inline-flex items-center rounded-xl wireframe-border bg-white px-4 py-2 text-sm text-zinc-900 shadow-sm transition-colors hover:bg-zinc-50"
          >
            {t("clearSearch")}
          </Link>
        ) : null}
      </div>
    </form>
  );
}
